/**
 * The purchase's form as the sheet holds it (LIS-1173, the purchase
 * design's §2.4 and §2.5), the same on the web and native: what the buyer
 * has typed, field by field (`FormDraft`), over the values the server's
 * read gives each field; the answers a start sends, read with the one
 * function the server reads them with (`readAnswers`); and what to say of
 * each problem. Every switch is over the closed kinds of the purchase.
 */
import {
  addressParts,
  countryOf,
  fieldsOf,
  formFor,
  readAnswers,
  readDestination,
  type AddressField,
  type AddressPart,
  type AnswerProblem,
  type Answers,
  type Destination,
  type Field,
  type Purchase,
  type Section,
} from "@libs/platform-wire/purchase";
import {
  countryData,
  diallingCode,
  isoCountryName,
  nationalPhone,
  phoneParts,
  postcodeNoun,
  readPhone,
} from "@libs/platform-wire/world";

/** An address as the form holds it: its country and each part as typed. */
export type AddressDraft = {
  country: string;
  line1: string;
  line2: string;
  city: string;
  region: string;
  postcode: string;
};

/** One field's value on the form, by its kind. */
export type FieldValue =
  | { kind: "text"; value: string }
  | { kind: "choice"; value: string }
  | { kind: "date"; value: string }
  | { kind: "phone"; country: string; number: string }
  | { kind: "address"; value: AddressDraft };

/** What the buyer has typed, by field id. A field they haven't touched is
 *  absent, so a new read's value fills it (§2.4 "Rereading"). */
export type FormDraft = Readonly<Record<string, FieldValue>>;

/** Where the buyer seems to be (the edge's country for the visitor): an
 *  address starts there when neither the read nor the field suggests one. */
export type FormContext = { country?: string | null };

const EMPTY_ADDRESS = { line1: "", line2: "", city: "", region: "", postcode: "" };

/** The country an address starts on: the buyer's own, the field's
 *  suggestion, else where the buyer seems to be where the field takes it. */
export function startingCountry(field: AddressField, context: FormContext): string {
  if (field.value) return field.value.country;
  if (field.suggested) return field.suggested;
  const near = context.country?.toUpperCase();
  return near && countryOf(field, near).kind !== "unlisted" ? near : "";
}

/** The address field of a purchase's form: every purchase asks one (its
 *  country, at least). */
export function addressFieldOf(sections: readonly Section[]): AddressField | null {
  for (const field of fieldsOf(sections)) if (field.kind === "address") return field;
  return null;
}

/** A field's value as the form shows it: what the buyer typed, else the
 *  read's (their saved answer), else empty. */
export function valueOf(field: Field, draft: FormDraft, context: FormContext, sections: readonly Section[] = []): FieldValue {
  const typed = draft[field.id];
  if (typed && typed.kind === field.kind) return typed;
  switch (field.kind) {
    case "text":
      return { kind: "text", value: field.value ?? "" };
    case "choice":
      return { kind: "choice", value: field.value ?? "" };
    case "date":
      return { kind: "date", value: field.value ?? "" };
    case "phone": {
      if (field.value) {
        const read = readPhone(field.value.number, field.value.country);
        return {
          kind: "phone",
          country: read?.country ?? field.value.country,
          number: read ? nationalPhone(read) : field.value.number,
        };
      }
      // A number is dialled where the address is, until the buyer says.
      const address = addressFieldOf(sections);
      const country = address ? addressValueOf(address, draft, context).country : "";
      return { kind: "phone", country: country || (context.country?.toUpperCase() ?? ""), number: "" };
    }
    case "address":
      return { kind: "address", value: addressValueOf(field, draft, context) };
  }
}

/** The address as the form shows it. */
export function addressValueOf(field: AddressField, draft: FormDraft, context: FormContext): AddressDraft {
  const typed = draft[field.id];
  if (typed?.kind === "address") return typed.value;
  const saved = field.value;
  return saved
    ? {
        country: saved.country,
        line1: saved.line1 ?? "",
        line2: saved.line2 ?? "",
        city: saved.city ?? "",
        region: saved.region ?? "",
        postcode: saved.postcode ?? "",
      }
    : { country: startingCountry(field, context), ...EMPTY_ADDRESS };
}

const orNull = (value: string) => (value.trim() ? value : null);

/** The answers the form gives, one per field it holds a value for: a part
 *  the chosen country's form hides is sent empty. */
export function answersOf(sections: readonly Section[], draft: FormDraft, context: FormContext): Answers {
  const answers: Answers = [];
  for (const field of fieldsOf(sections)) {
    const value = valueOf(field, draft, context, sections);
    switch (value.kind) {
      case "text":
      case "choice":
      case "date":
        answers.push({ id: field.id, answer: { value: orNull(value.value) } });
        break;
      case "phone":
        answers.push({
          id: field.id,
          answer: value.number.trim() && value.country ? { country: value.country, number: value.number } : null,
        });
        break;
      case "address": {
        if (field.kind !== "address" || !value.value.country) break;
        const form = formFor(field, value.value.country);
        const part = (name: AddressPart) => (form[name] ? orNull(value.value[name]) : null);
        answers.push({
          id: field.id,
          answer: {
            country: value.value.country,
            line1: part("line1"),
            line2: part("line2"),
            city: part("city"),
            region: part("region"),
            postcode: part("postcode"),
          },
        });
        break;
      }
    }
  }
  return answers;
}

/** A problem with one field, or one part of an address. */
export type FieldProblem = { part: AddressPart | "country" | null; code: AnswerProblem["code"] };

/** The form's problems by field, as the start would refuse them. */
export function problemsOf(sections: readonly Section[], answers: Answers, now?: Date): Map<string, FieldProblem[]> {
  const read = readAnswers(fieldsOf(sections), answers, now ? { now } : {});
  const problems = new Map<string, FieldProblem[]>();
  if (read.ok) return problems;
  for (const problem of read.problems) {
    const list = problems.get(problem.fieldId) ?? [];
    list.push({ part: problem.part, code: problem.code });
    problems.set(problem.fieldId, list);
  }
  return problems;
}

/** What the form asks of a field: all of it, or, of an address whose saved
 *  answer gives some of its parts, only the parts it lacks (the rest are
 *  summarised with the buyer's other saved answers). */
export type Ask = "whole" | readonly AddressPart[];

/** The fields the form asks, by id (`Ask`); every other one is summarised
 *  until the buyer edits. */
export type Asked = ReadonlyMap<string, Ask>;

/** The fields the read's saved answers don't give, which the form asks
 *  (every other one is summarised until the buyer edits): those whose saved
 *  answer the start would refuse, and an address whose country is only
 *  suggested, which the buyer has not given. Of an address that gives some
 *  of its parts, only the parts the start would refuse are asked. Decided on
 *  the read alone, so a field never turns into a summary as it is typed in. */
export function askedOf(sections: readonly Section[], context: FormContext): Map<string, Ask> {
  const problems = problemsOf(sections, answersOf(sections, {}, context));
  const asked = new Map<string, Ask>();
  for (const field of fieldsOf(sections)) {
    const found = problems.get(field.id);
    if (field.kind === "address") {
      const ask = addressAsk(field, found ?? []);
      if (ask) asked.set(field.id, ask);
    } else if (found) asked.set(field.id, "whole");
  }
  return asked;
}

/** What the form asks of an address, given what the start would refuse of
 *  its saved answer; null for nothing. */
function addressAsk(field: AddressField, problems: readonly FieldProblem[]): Ask | null {
  // An address with no saved value starts on a suggested country, which the
  // buyer has not given; one that gives no part beside its country stays
  // asked whole, as the read can't tell a saved country from where the
  // buyer seems to be.
  const saved = field.value;
  if (!saved || !shownParts(field, saved.country).some(({ part }) => saved[part]?.trim())) return "whole";
  if (problems.length === 0) return null;
  // Its parts are read in its country's form: a country to choose again
  // asks them all.
  const parts = new Set(problems.map((problem) => problem.part));
  if (parts.has(null) || parts.has("country")) return "whole";
  return addressParts.filter((part) => parts.has(part));
}

/** Every field of the sections, asked whole (what an attempt asks). */
export const askedWhole = (sections: readonly Section[]): Map<string, Ask> =>
  new Map(fieldsOf(sections).map((field) => [field.id, "whole" as const]));

/** The address parts asked of a field that is also summarised; null for a
 *  field asked whole or not asked. */
export function partsAsked(asked: Asked, field: Field): readonly AddressPart[] | null {
  const ask = asked.get(field.id);
  return ask && ask !== "whole" ? ask : null;
}

/** A line of the summary of a field the buyer's saved answers give, the
 *  same on the web and native; null for a field it can't summarise. An
 *  address leaves out the parts asked under the summary (`omit`). */
export function summaryOf(field: Field, value: FieldValue, omit: readonly AddressPart[] = []): string | null {
  switch (value.kind) {
    case "text":
    case "date":
      return value.value || null;
    case "choice":
      return field.kind === "choice" ? (field.options.find((option) => option.value === value.value)?.label ?? null) : null;
    case "phone": {
      // Hidden as a saved card is, all but its last digits: "+44 *******480".
      const read = readPhone(value.number, value.country);
      if (!read) return null;
      const { code, national } = phoneParts(read);
      return `+${code} ${"*".repeat(Math.max(0, national.length - 3))}${national.slice(-3)}`;
    }
    case "address": {
      const parts = addressParts
        .filter((part) => !omit.includes(part))
        .map((part) => value.value[part].trim())
        .filter(Boolean);
      return [...parts, isoCountryName(value.value.country) ?? value.value.country].join(", ");
    }
  }
}

/** The summary's lines, in the fields' order, the same on the web and
 *  native: each field's own (`summaryOf`), but a first name and the last
 *  name right after it read as one line ("Ada Lovelace"). */
export function summaryLines(lines: readonly { field: Field; line: string }[]): { id: string; line: string }[] {
  const named = (field: Field, autoComplete: string) => field.kind === "text" && field.autoComplete === autoComplete;
  const joined: { id: string; line: string }[] = [];
  lines.forEach(({ field, line }, index) => {
    const before = lines[index - 1];
    const last = joined.at(-1);
    if (last && before && named(before.field, "given-name") && named(field, "family-name")) last.line = `${last.line} ${line}`;
    else joined.push({ id: field.id, line });
  });
  return joined;
}

/** Whether the form's answers read: what Continue waits for. */
export const formComplete = (sections: readonly Section[], answers: Answers, now?: Date): boolean =>
  readAnswers(fieldsOf(sections), answers, now ? { now } : {}).ok;

const article = (noun: string) => (/^[aeiou]/i.test(noun) ? "an" : "a");

/**
 * What the form says under a field about a value it holds that the start
 * would refuse; null for a value not given yet (Continue waits for it) and
 * for a problem the form can't have made.
 */
export function problemWords(field: Field, problem: FieldProblem, value: FieldValue): string | null {
  switch (problem.code) {
    case "missing":
    case "unknown_field":
    case "duplicate_answer":
    case "kind_mismatch":
    case "hidden_part":
      return null;
    case "country_refused": {
      if (field.kind !== "address" || value.kind !== "address") return null;
      const listed = countryOf(field, value.value.country);
      return listed.kind === "refused" ? listed.message : null;
    }
    case "not_offered":
      return problem.part === "country" ? "Choose a country from the list." : "Choose one of the options.";
    case "invalid":
      switch (field.kind) {
        case "text":
          return field.message ?? `Check your ${field.label.toLowerCase()}.`;
        case "phone": {
          const country = value.kind === "phone" ? value.country : "";
          const name = country ? isoCountryName(country) : undefined;
          return name ? `Enter a full ${name} (${diallingCode(country)}) number.` : "Choose the number's country.";
        }
        case "date":
          return field.precision === "year" ? "Enter a year like 1990." : `Check your ${field.label.toLowerCase()}.`;
        case "address": {
          const country = value.kind === "address" ? value.value.country : "";
          if (problem.part === "postcode") {
            const noun = postcodeNoun(country);
            const example = countryData(country)?.postcode?.example;
            return example ? `Enter ${article(noun)} ${noun} like ${example}.` : `Check your ${noun}.`;
          }
          const shown = problem.part && problem.part !== "country" ? formFor(field, country)[problem.part] : null;
          return shown ? `Check your ${shown.label.toLowerCase()}.` : "Check your address.";
        }
        case "choice":
          return "Choose one of the options.";
      }
  }
}

/** The destination the address answers so far, for the store's delivery
 *  (§2.7): its country, postcode and region, nothing more. */
export function destinationOf(address: AddressDraft): Destination | null {
  if (!address.country) return null;
  return {
    country: address.country,
    postcode: address.postcode.trim() || null,
    region: address.region.trim() || null,
  };
}

/** Whether a destination is the buyer's own (a start takes an estimate
 *  only for one), or preliminary, asked about the country's stand-in; null
 *  where it doesn't read for the field. */
export function destinationKind(destination: Destination, field: AddressField): "buyer" | "preliminary" | null {
  const read = readDestination(destination, field);
  return read.kind === "refused" ? null : read.kind;
}

/** The address parts the form draws for its country, in order. */
export const shownParts = (field: AddressField, country: string) => {
  const form = formFor(field, country);
  return addressParts.flatMap((part) => {
    const shown = form[part];
    return shown ? [{ part, ...shown }] : [];
  });
};

/**
 * Whether the buyer's answers can turn a demo into a purchase: its address
 * reprices it, and it is a demo for the delivery country or its currency,
 * which another country's read may not be. A demo for its seller or for
 * postage not measured yet stays one wherever it goes, so the sheet shows
 * only why (§2.8).
 */
export function demoChangeable(purchase: Pick<Purchase, "sale" | "form">): boolean {
  switch (purchase.sale.kind) {
    case "buyable":
      return false;
    case "demo":
      switch (purchase.sale.reason) {
        case "country":
        case "currency":
          return !!addressFieldOf(purchase.form.sections)?.reprices;
        case "seller":
        case "postage_unmeasured":
          return false;
      }
  }
}

/** The section the purchase's names are in, for opening a seller account
 *  early (`prepare`): its first and last name answers, once both read. */
export function nameAnswers(purchase: Pick<Purchase, "form">, answers: Answers): Answers | null {
  const fields = fieldsOf(purchase.form.sections).filter((field) => field.id === "firstName" || field.id === "lastName");
  if (fields.length !== 2) return null;
  const named = answers.filter((answer) => answer.id === "firstName" || answer.id === "lastName");
  if (named.length !== 2 || !readAnswers(fields, named).ok) return null;
  return named;
}
